import { useState } from 'react';
import { Undo2, AlertTriangle } from 'lucide-react';
import { Modal } from '../Modal';
import { api } from '../../lib/api';
import { formatMoeda } from '@fiodecobre/shared';
import type { NotaFiscalRow } from '../../modules/notas-fiscais/NotasFiscaisLista';
import type { ResultadoDevolucao } from './ModalResultadoDevolucao';

interface Props {
  nota: NotaFiscalRow;
  // Modelo da nota de ORIGEM. A devolucao emitida e sempre NF-e mod 55 — o modelo 65
  // nao aceita nota de entrada. Muda o endpoint e o texto ([DA-440]).
  modelo: 'nfe' | 'nfce';
  onClose: () => void;
  // Entrega o resultado CRU pra tela abrir o modal de desfecho ([DA-443]) — nao uma
  // string de toast, que sumia antes de dar tempo de ler o protocolo.
  onSuccess: (resultado: ResultadoDevolucao) => void;
}

// Devolucao fiscal. Emite uma NF-e de ENTRADA (devolucao, finNFe=4) referenciando a
// nota original — desfaz o faturamento mesmo fora do prazo de cancelamento e devolve
// o produto ao estoque. So devolucao TOTAL (Fase 1).
// Origem NF-e mod 55 (Fase 1) ou NFC-e mod 65 (Fase 2, [DA-440]).
export function ModalDevolverNota({ nota, modelo, onClose, onSuccess }: Props) {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const minChars = 3;
  const valido = motivo.trim().length >= minChars;
  const numeroFmt = String(nota.numero).padStart(6, '0');
  const ehNfce = modelo === 'nfce';
  const rotulo = ehNfce ? 'NFC-e' : 'NF-e';

  async function confirmar() {
    if (!valido) return;
    setErro('');
    setLoading(true);
    try {
      const url = ehNfce
        ? `/api/nfe/devolucao-de-nfce/${nota.id}`
        : `/api/nfe/${nota.id}/devolucao`;
      const data = await api.post<ResultadoDevolucao & {
        autorizada?: boolean; xMotivo?: string; status?: string;
      }>(url, { motivo: motivo.trim() });
      if (data.autorizada) {
        onSuccess(data);
      } else {
        setErro(`SEFAZ nao autorizou a devolucao: ${data.xMotivo || 'erro desconhecido'}${data.cStat ? ` (cStat ${data.cStat})` : ''}`);
      }
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      aberto
      onFechar={onClose}
      titulo={`Emitir devolucao da ${rotulo}`}
      largura={540}
      rodape={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} disabled={loading} className="btn">Voltar</button>
          <button
            type="button"
            onClick={() => void confirmar()}
            disabled={!valido || loading}
            className="btn btn-primary"
          >
            <Undo2 size={14} style={{ display: 'inline', marginRight: 6, marginBottom: -2 }} />
            {loading ? 'Emitindo...' : 'Emitir devolucao'}
          </button>
        </div>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 13 }}>
          <div><strong>{rotulo} original:</strong> {numeroFmt} / Serie {nota.serie}</div>
          <div style={{ marginTop: 2 }}>
            <strong>Cliente:</strong> {nota.cliente_razao || nota.cliente_nome || 'Consumidor'}
          </div>
          <div style={{ marginTop: 2 }}>
            <strong>Valor:</strong> {formatMoeda(nota.valor_total)}
          </div>
          <div style={{ marginTop: 2 }}>
            <strong>Chave:</strong>{' '}
            <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{nota.chave}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fde68a', border: '1px solid #f59e0b', borderRadius: 6, padding: 10, fontSize: 12, color: '#000' }}>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            Emite uma <strong>NF-e de devolucao (entrada)</strong> referenciando esta nota,
            zerando o faturamento dela na SEFAZ e <strong>devolvendo os produtos ao estoque</strong>.
            Use quando o prazo de cancelamento ja passou{ehNfce ? ' (NFC-e vale so 30 minutos)' : ''}.{' '}
            <strong>Devolve a nota inteira</strong> (total).
            Nao mexe na venda, no crediario nem na comissao.
            {ehNfce && (
              <> A devolucao sai como <strong>NF-e modelo 55 em nome da propria loja</strong> — a NFC-e
              de balcao nao identifica o consumidor, e quem devolve nao emite nota.</>
            )}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            Motivo da devolucao
            <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (min. {minChars})</span>
          </label>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value.slice(0, 240))}
            placeholder="Ex: nota duplicada, venda cancelada fora do prazo"
            rows={3}
            style={{
              width: '100%',
              border: '1px solid var(--input-border)',
              borderRadius: 4,
              padding: '6px 8px',
              fontSize: 13,
              resize: 'vertical',
            }}
            autoFocus
            disabled={loading}
          />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
            {motivo.trim().length} caracteres
          </div>
        </div>

        {erro && (
          <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 4, padding: 8, fontSize: 12, color: '#991b1b' }}>
            {erro}
          </div>
        )}
      </div>
    </Modal>
  );
}
